import { memo, useCallback, useMemo, useState } from "react";
import { theme } from "twin.macro";
import Button from "@atoms/Button";
import Paragraph from "@atoms/Paragraph";
import CounterInput from "@atoms/CounterInput";
// import ProductCardTag from "@atoms/ProductCardTag";
// import Caption from "@atoms/Caption";
import Caption from "@/components/Atoms/Caption";
import Label from "@/components/Atoms/Label";
import Image from "@atoms/Image";
import TagsVR3 from "@atoms/TagsVR3";
import ModalItemsOutOfStock from "@/components/Organisms/ModalItemsOutOfStock";
import IconCart from "@iconsV2/Interrface/cart-01.svg?react";
import useAppSelector from "@/hooks/useAppSelector";
import useProductCardGallery from "@hooks/useProductCardGallery";
import { MAX_UNIT_QUANTITY } from "@/recourses/constants";

import { generateProductUrl, typeDiscountName } from "@/utils/product";
import type { IProductCardGallery } from "@/types/productGallery";
import { getDisplayBrand } from "@/utils/getDisplayBrand";
import { formatCurrency } from "@utils/money";
import { TYPE_DISCOUNT } from "@/services/checkout/type";
import {
  StyleButton,
  GroupContentWrapper,
  StyledWrapperButton,
  StyledWrapperCard,
  StyledWrapperContent,
  StyledWrapperContentDiscount,
  StyledWrapperContentPrice,
  StyledWrapperImage,
  StyledWrapperFigure,
  StyledWrapperPriceBefore,
  StyledWrapperContentPriceRoot,
} from "./styles";

const ProductCardGalleryXL = ({
  item,
  orientation = "vertical",
  recommId,
  active = false,
  disabled = false,
  withBorder,
  ...rest
}: Omit<IProductCardGallery, "size" | "sizeVariant">) => {
  const {
    // states:
    showInput,
    productQuantity,
    isProductAvailable,
    percentageDiscount,
    productFinalPrice,
    productOriginalPrice,
    productFinalPricePerStepWeight,
    gramOrUnitShort,

    // functions:
    getGramans,
    handleAddToCart,
    handledToCleanCart,
    handleIncrement,
    handleToDecrement,
    handleToChangeInput,
    handleGoToBrand,
  } = useProductCardGallery({
    item: { ...item, recommId },
  });

  const [openModalOut, setOpenModalOut] = useState(false);
  const storeId = useAppSelector((state) => state.cart.storeId);

  const isDisabled = disabled || !isProductAvailable;
  const customLabelOrDiscount = useMemo(() => {
    const titleLabels: string[] = [];
    if (item.details.isNew) titleLabels.push("Nuevo");
    if (item.details.isPromoted) titleLabels.push("Promocionado");
    return titleLabels;
  }, [item.details.isPromoted, item.details.isNew]);

  const handleAlternatives = useCallback(() => {
    setOpenModalOut(true);
  }, []);

  const TagCustomLabelBesSeller = useCallback(() => {
    // if (product?.bestSeller) {
    //  falta implementar el componente
    // return (
    //   <span className="wrapper-custom-label">
    //     <ProductCardTag type="bestSeller" />
    //   </span>
    // );
    // }
    if (customLabelOrDiscount.length > 0) {
      return (
        <span className="wrapper-tag-new">
          {customLabelOrDiscount && <TagsVR3 label={customLabelOrDiscount} type="sponsor" separator="dot" />}
        </span>
      );
    }
    return null;
  }, [
    // product?.bestSeller,
    customLabelOrDiscount,
  ]);

  const displayCustomLabelOrDiscountTags = () => {
    return (
      <span tw="mt-xxs w-max">
        {item.details.customLabel ? (
          <TagsVR3
            type={item.details.customLabel === TYPE_DISCOUNT.CUSTOMER_DISCOUNT ? "discount" : "customLabel"}
            label={[
              typeDiscountName[item.details.customLabel] ?? item.details.customLabel,
              `- ${percentageDiscount}%`,
            ]}
            color={item.details.customLabelBackgroundColor}
          />
        ) : (
          percentageDiscount > 0 && (
            <TagsVR3 type="discount" label={["Promofit", `- ${percentageDiscount}%`]} />
          )
        )}
      </span>
    );
  };
  if (!item) {
    return null;
  }

  return (
    <>
      <ModalItemsOutOfStock
        isOpen={openModalOut}
        handleClose={() => setOpenModalOut(false)}
        closeOutSise
        onlyClose
        storeId={storeId}
        productToVerify={item}
      />

      <StyledWrapperCard
        $orientation={orientation}
        $showInputCounter={showInput}
        $isPromoted={item.details.isPromoted}
        $active={active}
        to={generateProductUrl(item.product.slug, recommId)}
        title={item.product.name}
        $withBorder={withBorder}
        state={{
          product: item,
        }}
        {...rest}
      >
        <StyledWrapperImage $orientation={orientation}>
          <StyledWrapperFigure $disabled={isDisabled}>
            <TagCustomLabelBesSeller />

            <Image
              src={item.product.image.url}
              alt={item.product.image.alt ?? item.product.name}
              title={item.product.image.alt ?? item.product.name}
              effect="blur"
            />
          </StyledWrapperFigure>

          {/* Time limited */}
          {/*  <div tw="w-full bg-neutral-90 p-xxs flex justify-center items-center">
          <Caption>Por tiempo limitado</Caption>
        </div> */}
        </StyledWrapperImage>

        <div tw="w-full h-full">
          <StyledWrapperContent>
            <GroupContentWrapper tw="border-b border-neutral-90 pb-xxs">
              <Paragraph sizeVariant="md" tw="line-clamp-2" disabled={isDisabled}>
                {item.product.name}
              </Paragraph>

              <Paragraph
                sizeVariant="sm"
                fontWeight={600}
                color={theme`colors.neutral.10`}
                onClick={handleGoToBrand}
                tw="cursor-pointer"
                disabled={isDisabled}
              >
                {getDisplayBrand(item.product.brand.name)}
              </Paragraph>
            </GroupContentWrapper>

            <StyledWrapperContentPriceRoot $orientation={orientation}>
              {productQuantity > (item.details.averageWeight || 1) && (
                <StyledWrapperContentPrice>
                  <Caption
                    color={theme`colors.neutral.40`}
                    disabled={isDisabled}
                    fontWeight={600}
                    size="small"
                  >
                    {formatCurrency(productFinalPricePerStepWeight ?? 0)} /{gramOrUnitShort}
                  </Caption>
                </StyledWrapperContentPrice>
              )}

              <StyledWrapperContentPrice>
                <StyledWrapperContentDiscount $orientation={orientation}>
                  <Label sizeVariant="lg" color={theme`colors.neutral.10`} disabled={isDisabled}>
                    {formatCurrency(productFinalPrice ?? 0)}
                  </Label>
                </StyledWrapperContentDiscount>

                {item.details.onSale && (
                  <StyledWrapperPriceBefore>
                    <Label
                      sizeVariant="lg"
                      color={theme`colors.neutral.50`}
                      tw="line-through"
                      disabled={isDisabled}
                    >
                      {formatCurrency(productOriginalPrice ?? 0)}
                    </Label>
                  </StyledWrapperPriceBefore>
                )}
              </StyledWrapperContentPrice>

              {displayCustomLabelOrDiscountTags()}
            </StyledWrapperContentPriceRoot>

            <div tw="flex flex-col justify-between flex-1">
              <div tw="flex flex-col h-full items-end mt-xxs">
                <StyledWrapperButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  $orientation={orientation}
                >
                  <GroupContentWrapper>
                    {(((item.details.maxQtyToOrder < MAX_UNIT_QUANTITY ||
                      productQuantity > item.details.maxQtyToOrder) &&
                      item.details.maxQtyToOrder > 0) ||
                      !isProductAvailable) && (
                      <Paragraph
                        sizeVariant="sm"
                        color={
                          productQuantity > item.details.maxQtyToOrder || !isProductAvailable
                            ? theme`colors.error.40`
                            : theme`colors.neutral.10`
                        }
                        textAlign="center"
                      >
                        {isProductAvailable
                          ? `Quedan ${item.details.maxQtyToOrder} ${getGramans(+item.details.maxQtyToOrder)}`
                          : "No disponible"}
                      </Paragraph>
                    )}
                    {!isProductAvailable || item.details.maxQtyToOrder === 0 ? (
                      <Button color="secondary" sizeVariant="md" fullWidth onClick={handleAlternatives}>
                        Ver alternativas
                      </Button>
                    ) : (
                      <StyleButton>
                        {isProductAvailable ? (
                          <>
                            {showInput ? (
                              <div tw="flex flex-col gap-xs">
                                <CounterInput
                                  value={String(productQuantity)}
                                  showIconRemove
                                  onRemove={handledToCleanCart}
                                  onIncrement={() => handleIncrement(+item.product.sku)}
                                  onDecrement={handleToDecrement}
                                  onChange={(value) => handleToChangeInput(value as number)}
                                  maxUnits={item.details.maxQtyToOrder}
                                  id="counter-input"
                                  error={productQuantity > item.details.maxQtyToOrder}
                                  measureType={item.details.measureTypeDisplayShort ?? ""}
                                  size="large"
                                  averageWeight={item.details.averageWeight || 1}
                                />
                              </div>
                            ) : (
                              <Button
                                fullWidth
                                sizeVariant="md"
                                endIcon={<IconCart />}
                                disabled={isDisabled}
                                onClick={handleAddToCart}
                              >
                                Agregar
                              </Button>
                            )}
                          </>
                        ) : (
                          <div />
                        )}
                      </StyleButton>
                    )}
                  </GroupContentWrapper>
                </StyledWrapperButton>
              </div>
            </div>
          </StyledWrapperContent>
        </div>
      </StyledWrapperCard>
    </>
  );
};

export default memo(ProductCardGalleryXL, (prevProps, nextProps) => {
  return (
    prevProps.item.product.id === nextProps.item.product.id &&
    prevProps.orientation === nextProps.orientation &&
    prevProps.item.details.maxQtyToOrder === nextProps.item.details.maxQtyToOrder
  );
});
